import { BodyText, Button, Strong } from '@utilitywarehouse/hearth-react';
import { useEffect } from 'react';

import { Dialog, DialogActions, DialogID, useDialog } from '@components/Dialog';
import { ProductType } from '@generated/gql/graphql';

interface RemoveDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onRemove: () => void;
  productType: ProductType;
  productName?: string;
}

interface RemoveDialogUIProps {
  dialogId: DialogID;
  productName: string;
  buttonLabel: string;
  onClose: () => void;
  onRemove: () => void;
}

const RemoveDialogUI = ({
  dialogId,
  productName,
  buttonLabel,
  onClose,
  onRemove,
}: RemoveDialogUIProps) => (
  <Dialog
    id={dialogId}
    heading="Are you sure?"
    onClose={onClose}
    preventDismiss
    hideCloseButton
    data-testid="remove-dialog"
  >
    <BodyText>
      Are you sure you want to remove <Strong>{productName}</Strong> from your
      bundle?
    </BodyText>
    <DialogActions>
      <Button
        variant="ghost"
        colorScheme="destructive"
        onClick={onRemove}
        data-testid="remove-dialog-remove-button"
      >
        Yes, remove {buttonLabel}
      </Button>
      <Button
        variant="solid"
        onClick={onClose}
        data-testid="remove-dialog-keep-button"
      >
        No, keep {buttonLabel}
      </Button>
    </DialogActions>
  </Dialog>
);

const RemoveDialog: React.FC<RemoveDialogProps> = ({
  isOpen,
  onClose,
  onRemove,
  productType,
  productName: productNameOverride,
}) => {
  const dialog = useDialog(DialogID.REMOVE_FROM_BUNDLE);
  const productName = productNameOverride || productType.toLowerCase();
  const buttonLabel = productNameOverride ? '' : productName;

  useEffect(() => {
    dialog.setOpen(isOpen);
  }, [dialog, isOpen]);

  return (
    <RemoveDialogUI
      dialogId={dialog.id}
      productName={productName}
      buttonLabel={buttonLabel}
      onClose={onClose}
      onRemove={onRemove}
    />
  );
};

export type { RemoveDialogProps, RemoveDialogUIProps };

export { RemoveDialog, RemoveDialogUI };
